/**
 * ExecutionCell Component
 *
 * PURPOSE:
 * This component displays the execution status for a single test case or test step
 * in a specific test run. It shows either:
 * - A status badge (if the test has been executed)
 * - Quick action buttons to set status (if not yet executed)
 *
 * VISUAL BEHAVIOR:
 * - When hovering over an execution cell, Pass/Fail/More buttons fade in
 * - Clicking Pass or Fail immediately updates the status via API
 * - Clicking More (⋮) shows a dropdown with other available statuses
 *
 * KEY FEATURES:
 * - Uses Atlaskit Button components for Jira-native look and feel
 * - Bold colors (#5B7F24 green for Pass, #C9372C red for Fail)
 * - Dropdown auto-closes when clicking outside or selecting a status
 * - Prevents event propagation to avoid opening edit modal when clicking buttons
 */

import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateTestCaseExecutionAsync, updateTestStepExecutionAsync } from '../store/slices/testScenarioSlice'
import { selectProjectStatuses } from '../store/slices/projectStatusesSlice'
import Button from '@atlaskit/button'
import MoreIcon from '@atlaskit/icon/glyph/more'
import { token } from '@atlaskit/tokens'
import { useNotification } from '../contexts/NotificationContext'

// Status icons
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle'
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle'
import WarningIcon from '@atlaskit/icon/glyph/warning'
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle'
import EditorHorizontalRuleIcon from '@atlaskit/icon/glyph/editor/horizontal-rule'

// Action icons for Pass/Fail icon-only buttons (distinct from status display icons)
import EditorDoneIcon from '@atlaskit/icon/glyph/editor/done'
import EditorErrorIcon from '@atlaskit/icon/glyph/editor/error'

// Metadata icons (from @atlaskit/icon/core for proper Jira look)
import CommentIcon from '@atlaskit/icon/core/comment'
import BugIcon from '@atlaskit/icon/core/bug'
import AttachmentIcon from '@atlaskit/icon/core/attachment'
import Tooltip from '@atlaskit/tooltip'

/**
 * ExecutionCell Component
 *
 * @param {number} testCaseId - ID of the test case this execution belongs to
 * @param {number} testStepId - ID of the test step (if this is a step execution, otherwise undefined)
 * @param {number} runId - ID of the test run this execution is for
 * @param {object} execution - Execution object if status has been set (contains statusId, statusName, statusColor)
 * @param {boolean} isTestCase - True if this is a test case execution, false if test step execution
 * @param {function} onExecutionClick - Callback to open execution details modal when clicking on a cell with status
 */
export function ExecutionCell({ testCaseId, testStepId, runId, execution, isTestCase, onExecutionClick }) {
  // Redux hooks for dispatching actions and accessing global state
  const dispatch = useDispatch()

  // Get all available status options for this project (Pass, Fail, Block, Pending, etc.)
  const projectStatuses = useSelector(selectProjectStatuses)

  // Get the current test scenario ID (needed for API calls)
  const testScenarioId = useSelector(state => state.testScenario.testScenarioId)

  // Get notification function for showing error messages
  const { showError } = useNotification()

  /**
   * STATE MANAGEMENT
   */

  // Controls whether the dropdown menu is open or closed
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Reference to the dropdown container div (used for click-outside detection)
  const dropdownRef = useRef(null)

  // Track which button (pass/fail) is being hovered for dynamic styling
  const [hoveredButton, setHoveredButton] = useState(null) // 'pass', 'fail', or null

  /**
   * EFFECT: Click Outside Detection
   *
   * When the dropdown is open, this effect adds a mousedown listener to the entire document.
   * If the user clicks anywhere outside the dropdown, it automatically closes.
   *
   * This creates better UX by allowing users to dismiss the dropdown naturally.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click target is outside the dropdown container
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    // Only add the listener when dropdown is open
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)

      // Cleanup: remove the listener when component unmounts or dropdown closes
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen]) // Re-run when isDropdownOpen changes

  /**
   * MAP STATUS NAME TO ICON COMPONENT
   *
   * Returns the appropriate icon component for a given status name.
   * Used for both the execution cell display and dropdown menu.
   *
   * @param {string} statusName - Name of the status (e.g., "Pass", "Fail", "Block")
   * @returns {React.Component} The corresponding icon component
   */
  const getStatusIcon = (statusName) => {
    const name = statusName?.toLowerCase()

    if (name === 'pass' || name === 'passed') return CheckCircleIcon
    if (name === 'fail' || name === 'failed') return CrossCircleIcon
    if (name === 'block' || name === 'blocked') return WarningIcon
    if (name === 'query') return QuestionCircleIcon
    if (name === 'exclude' || name === 'excluded') return EditorHorizontalRuleIcon

    // Default fallback (shouldn't happen with fixed statuses)
    return CheckCircleIcon
  }

  /**
   * GET ICON COLOR FOR STATUS
   *
   * Returns the color to use for status icons.
   * Uses Atlaskit design tokens for theme-aware colors.
   * Falls back to execution.statusColor if available for custom statuses.
   *
   * @param {string} statusName - Name of the status
   * @param {string} statusColor - Color from execution object (optional)
   * @returns {string} CSS color value for the icon
   */
  const getStatusIconColor = (statusName, statusColor) => {
    // Use semantic tokens for known status types (theme-aware)
    const name = statusName?.toLowerCase()
    if (name === 'pass' || name === 'passed') return token('color.icon.success')
    if (name === 'fail' || name === 'failed') return token('color.icon.danger')
    if (name === 'block' || name === 'blocked') return token('color.icon.warning')
    if (name === 'query') return token('color.icon.information')
    if (name === 'exclude' || name === 'excluded') return token('color.icon.subtle')

    // Use the color from execution object if available for custom statuses
    if (statusColor) return statusColor

    return token('color.icon.subtle') // Default subtle gray
  }

  /**
   * CHECK FOR METADATA
   *
   * Determine if execution has comments, bugs, or attachments.
   * These will be shown as indicator icons next to the status icon.
   * API returns counts: commentCount, bugCount, attachmentCount
   */
  const commentCount = execution?.commentCount || 0
  const bugCount = execution?.bugCount || 0
  const attachmentCount = execution?.attachmentCount || 0


  /**
   * FIND PASS AND FAIL STATUSES (MEMOIZED)
   *
   * Search through all project statuses to find the Pass and Fail options.
   * These are shown as primary quick-action buttons since they're used most frequently.
   *
   * OPTIMIZATION: useMemo ensures this calculation only runs once when projectStatuses loads,
   * not on every hover/render. This significantly improves performance.
   *
   * Note: We check for both "pass"/"passed" and "fail"/"failed" to handle API variations
   */
  const passStatus = React.useMemo(() =>
    projectStatuses.find(status =>
      status.name?.toLowerCase() === 'pass' ||
      status.name?.toLowerCase() === 'passed'
    ),
    [projectStatuses]
  )

  const failStatus = React.useMemo(() =>
    projectStatuses.find(status =>
      status.name?.toLowerCase() === 'fail' ||
      status.name?.toLowerCase() === 'failed'
    ),
    [projectStatuses]
  )

  /**
   * FILTER OTHER STATUSES (MEMOIZED)
   *
   * Get all statuses that aren't Pass or Fail (e.g., Block, Pending, In Progress)
   * These will be shown in the dropdown menu when user clicks the More (⋮) button
   *
   * OPTIMIZATION: useMemo ensures this filtering only runs once, not on every hover
   */
  const otherStatuses = React.useMemo(() =>
    projectStatuses.filter(status =>
      status.name?.toLowerCase() !== 'pass' &&
      status.name?.toLowerCase() !== 'passed' &&
      status.name?.toLowerCase() !== 'fail' &&
      status.name?.toLowerCase() !== 'failed'
    ),
    [projectStatuses]
  )


  /**
   * INLINE STYLE OBJECTS FOR PASS/FAIL BUTTONS
   *
   * Using Atlaskit design tokens for theme-aware styling.
   * Base styles: Subtle green/red backgrounds with colored borders (Jira lozenge style)
   * Hover styles: Darker backgrounds + subtle box shadow for elevation effect
   */
  const passBaseStyle = {
    backgroundColor: token('color.background.success'),      // Light green background
    color: token('color.text.success'),                       // Dark green text
    border: `1px solid ${token('color.border.success')}`,     // Green border
    minWidth: '28px',                                         // Icon button sized to fit row height
    minHeight: '28px',
    padding: '4px',
    transition: 'all 0.2s ease',
    boxShadow: 'none'
  }

  const passHoverStyle = {
    backgroundColor: token('color.background.success.hovered'),  // Darker green on hover
    color: token('color.text.success'),
    border: `1px solid ${token('color.border.success')}`,
    minWidth: '28px',
    minHeight: '28px',
    padding: '4px',
    transition: 'all 0.2s ease',
    boxShadow: token('elevation.shadow.raised')  // Subtle shadow for elevation
  }

  const failBaseStyle = {
    backgroundColor: token('color.background.danger'),        // Light red background
    color: token('color.text.danger'),                        // Dark red text
    border: `1px solid ${token('color.border.danger')}`,      // Red border
    minWidth: '28px',
    minHeight: '28px',
    padding: '4px',
    transition: 'all 0.2s ease',
    boxShadow: 'none'
  }

  const failHoverStyle = {
    backgroundColor: token('color.background.danger.hovered'),  // Darker red on hover
    color: token('color.text.danger'),
    border: `1px solid ${token('color.border.danger')}`,
    minWidth: '28px',
    minHeight: '28px',
    padding: '4px',
    transition: 'all 0.2s ease',
    boxShadow: token('elevation.shadow.raised')  // Subtle shadow for elevation
  }

  /**
   * HANDLE STATUS CLICK
   *
   * This function is called when user clicks any status button (Pass, Fail, or dropdown item).
   * It dispatches the appropriate Redux action to update the execution status via API.
   *
   * FLOW:
   * 1. Validate that status exists and testScenarioId is available
   * 2. Build params object with all necessary data
   * 3. Dispatch either updateTestCaseExecutionAsync or updateTestStepExecutionAsync
   * 4. Redux thunk handles the API call and updates the store
   * 5. Component re-renders with new status badge
   *
   * @param {object} status - The status object being set (contains id, name, color)
   */
  const handleStatusClick = async (status) => {
    // Validation: Ensure status exists
    if (!status) {
      console.error('Status not found in project statuses')
      return
    }

    // Validation: Ensure we have a test scenario ID
    if (!testScenarioId) {
      console.error('testScenarioId is missing')
      return
    }

    // Build the parameters object for the API call
    const params = {
      testScenarioId,     // Which test scenario this belongs to
      testRunId: runId,   // Which test run this execution is for
      testCaseId,         // Which test case (always provided)
      statusId: status.id,       // The ID of the selected status
      statusName: status.name,   // The name for display (e.g., "Pass")
      statusColor: status.color  // The color code for the badge (e.g., "#5B7F24")
    }

    try {
      // Dispatch the appropriate async action based on whether this is a test case or test step
      if (isTestCase) {
        // Update test case execution - unwrap() will throw if rejected
        await dispatch(updateTestCaseExecutionAsync(params)).unwrap()
      } else {
        // Update test step execution (includes testStepId) - unwrap() will throw if rejected
        await dispatch(updateTestStepExecutionAsync({
          ...params,
          testStepId
        })).unwrap()
      }

      // Success - no notification needed (user requirement: errors only)

    } catch (error) {
      // Error occurred - show notification to user
      const errorMessage = error.message || 'Failed to update execution status'

      showError(
        'Failed to Update Status',
        errorMessage
      )

      // Log error for debugging
      console.error('Status update failed:', error)

      // Optimistic update rollback happens automatically via Redux
      // because the async thunk was rejected and state wasn't updated
    }
  }

  /**
   * RENDER: STATUS ICON + METADATA INDICATORS (When execution has a valid status set)
   *
   * If the test has already been executed AND has a valid status, show:
   * - Status icon (medium size, colored based on status)
   * - Metadata indicators (comment, bug, attachment icons) to the right
   *
   * IMPORTANT: We check for execution.statusId, not just execution existence.
   * When status is cleared, execution object may still exist with null values.
   * If statusId is null, we should show Pass/Fail buttons (blank state) instead.
   *
   * This design saves space by using icons instead of text.
   */
  if (execution && execution.statusId) {
    // Get the appropriate icon component and color for this status
    const StatusIcon = getStatusIcon(execution.statusName)
    const iconColor = getStatusIconColor(execution.statusName, execution.statusColor)

    /**
     * HANDLE EXECUTION CLICK
     *
     * Opens modal to view/edit execution details (status, comments, bugs, attachments).
     * Uses e.stopPropagation() to prevent row's onMouseDown from firing.
     */
    const handleExecutionClick = (e) => {
      e.stopPropagation()  // Prevent row edit modal from opening

      // Call parent handler to open modal
      // Parent (App.jsx) will set modal state
      if (onExecutionClick) {
        onExecutionClick(e, {
          testCaseId,
          testStepId,
          runId,
          execution
        })
      }
    }

    return (
      <div
        className="execution-controls"
        onClick={handleExecutionClick}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', position: 'relative' }}
      >
        {/* Status icon - always centered in cell */}
        <StatusIcon
          label={execution.statusName}
          size="medium"
          primaryColor={iconColor}
        />

        {/* Metadata indicators - positioned to the right of center, doesn't affect status centering */}
        {/* These icons indicate presence of comments/bugs/attachments - click passes through to open modal */}
        {(commentCount > 0 || bugCount > 0 || attachmentCount > 0) && (
          <div
            className="metadata-indicators"
            style={{
              position: 'absolute',
              left: '50%',
              marginLeft: '14px', // Start after the centered status icon (icon is ~24px, so half + small gap)
              display: 'flex',
              gap: '2px',
              alignItems: 'center'
            }}
          >
            {/* Comment icon - shown only if execution has comments */}
            {/* Using small size and scale transform for subtle appearance */}
            {commentCount > 0 && (
              <Tooltip content={`${commentCount} comment(s)`}>
                <span style={{ display: 'flex', alignItems: 'center', transform: 'scale(0.75)', cursor: 'default' }}>
                  <CommentIcon label="Comments" size="small" color={token('color.icon.subtle')} />
                </span>
              </Tooltip>
            )}

            {/* Bug icon - shown only if execution has bugs linked */}
            {/* Using small size and scale transform for subtle appearance */}
            {bugCount > 0 && (
              <Tooltip content={`${bugCount} bug(s) linked`}>
                <span style={{ display: 'flex', alignItems: 'center', transform: 'scale(0.75)', cursor: 'default' }}>
                  <BugIcon label="Bugs" size="small" color={token('color.icon.subtle')} />
                </span>
              </Tooltip>
            )}

            {/* Attachment icon - shown only if execution has attachments */}
            {/* Using small size and scale transform for subtle appearance */}
            {attachmentCount > 0 && (
              <Tooltip content={`${attachmentCount} attachment(s)`}>
                <span style={{ display: 'flex', alignItems: 'center', transform: 'scale(0.75)', cursor: 'default' }}>
                  <AttachmentIcon label="Attachments" size="small" color={token('color.icon.subtle')} />
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    )
  }

  /**
   * RENDER: QUICK ACTION BUTTONS (When no execution exists yet)
   *
   * If the test hasn't been executed, show interactive buttons that appear on hover.
   *
   * INTERACTION FLOW:
   * 1. User hovers over execution cell → Buttons fade in (opacity 0 → 1)
   * 2. User can click:
   *    - Pass button → Sets status to Pass
   *    - Fail button → Sets status to Fail
   *    - More (⋮) button → Opens dropdown with other statuses
   * 3. User moves mouse away → Buttons fade out (opacity 1 → 0)
   *    AND dropdown closes (if not hovering over dropdown menu)
   */
  return (
    <div
      className={`execution-controls ${isDropdownOpen ? 'dropdown-open' : ''}`}
      onMouseLeave={(e) => {
        /**
         * SMART DROPDOWN CLOSE
         *
         * Close the dropdown when mouse leaves execution-controls, UNLESS
         * we're moving to the dropdown menu itself (which is a child element).
         *
         * Uses relatedTarget to check where the mouse is going:
         * - If moving to dropdown menu → keep open
         * - If moving anywhere else (another row, outside) → close
         */
        if (isDropdownOpen) {
          const relatedTarget = e.relatedTarget
          // Check if we're moving to the dropdown menu (child of dropdownRef)
          if (relatedTarget && dropdownRef.current && dropdownRef.current.contains(relatedTarget)) {
            return // Moving to dropdown menu, keep it open
          }
          // Otherwise close - moving to another row or outside the cell
          setIsDropdownOpen(false)
        }
      }}
    >
      {/*
        PASS BUTTON (Icon-only with Tooltip)

        Atlaskit Button with EditorDoneIcon, wrapped in Tooltip.
        Shows "Pass" tooltip on hover for discoverability.
        Green background/border for visual clarity.
      */}
      <Button
        appearance="default"         // Atlaskit button style
        spacing="compact"             // Smaller padding for tables
        className="control-button pass"  // CSS class for opacity control (hover in/out)
        onClick={(e) => {
          e.stopPropagation()         // Prevent row click (which opens edit modal)
          handleStatusClick(passStatus)  // Set status to Pass
        }}
        onMouseDown={(e) => e.stopPropagation()}  // Also prevent mousedown propagation
        onMouseEnter={() => setHoveredButton('pass')}  // Track hover for style change
        onMouseLeave={() => setHoveredButton(null)}    // Clear hover state
        isDisabled={!passStatus}      // Disable if Pass status not found in project
        style={hoveredButton === 'pass' ? passHoverStyle : passBaseStyle}  // Conditional styling
        iconBefore={<EditorDoneIcon label="Pass" size="medium" />}  // Checkmark icon
      />

      {/*
        FAIL BUTTON (Icon-only with Tooltip)

        Atlaskit Button with EditorErrorIcon, wrapped in Tooltip.
        Shows "Fail" tooltip on hover for discoverability.
        Red background/border for visual clarity.
      */}
      <Button
        appearance="default"         // Atlaskit button style
        spacing="compact"             // Smaller padding for tables
        className="control-button fail"  // CSS class for opacity control (hover in/out)
        onClick={(e) => {
          e.stopPropagation()         // Prevent row click (which opens edit modal)
          handleStatusClick(failStatus)  // Set status to Fail
        }}
        onMouseDown={(e) => e.stopPropagation()}  // Also prevent mousedown propagation
        onMouseEnter={() => setHoveredButton('fail')}  // Track hover for style change
        onMouseLeave={() => setHoveredButton(null)}    // Clear hover state
        isDisabled={!failStatus}      // Disable if Fail status not found in project
        style={hoveredButton === 'fail' ? failHoverStyle : failBaseStyle}  // Conditional styling
        iconBefore={<EditorErrorIcon label="Fail" size="medium" />}  // Cross icon
      />

      {/*
        MORE BUTTON + DROPDOWN MENU

        Only shown if there are other statuses besides Pass/Fail
        (e.g., Block, Pending, In Progress)

        STRUCTURE:
        - Wrapper div with position: relative (for dropdown positioning)
        - Atlaskit Button with ⋮ icon (subtle appearance)
        - Dropdown menu (conditionally rendered when isDropdownOpen is true)
      */}
      {otherStatuses.length > 0 && (
        <div className="control-button more" ref={dropdownRef} style={{ position: 'relative' }}>
          {/*
            MORE BUTTON (⋮)

            Subtle Atlaskit button with icon-only design
            Clicking toggles the dropdown menu open/closed
          */}
          <Button
            appearance="subtle"           // Minimal visual weight (transparent → light gray hover)
            spacing="compact"             // Compact for table layout
            className="control-button-inner"  // CSS class for styling
            iconBefore={<MoreIcon label="More statuses" size="small" />}  // ⋮ icon
            onClick={(e) => {
              e.stopPropagation()         // Prevent row click
              setIsDropdownOpen(!isDropdownOpen)  // Toggle dropdown
            }}
            onMouseDown={(e) => e.stopPropagation()}  // Prevent mousedown propagation
          />

          {/*
            DROPDOWN MENU

            Only rendered when isDropdownOpen is true
            Shows ALL statuses (including Pass and Fail) with icon + text

            POSITIONING:
            - position: absolute → Taken out of normal flow
            - top: 100% → Positioned below the button
            - right: 0 → Aligned to right edge
            - marginTop: 4px → Small gap between button and menu
          */}
          {isDropdownOpen && (
            <div
              style={{
                position: 'absolute',       // Position relative to parent
                top: '100%',                // Below the button
                right: 0,                   // Align right
                paddingTop: '4px',          // Invisible bridge to cover gap (prevents close on gap hover)
                zIndex: 1000                // Ensure menu appears above other content
              }}
            >
              {/* Inner container for visual styling - separated from positioning */}
              <div
                style={{
                  backgroundColor: token('elevation.surface.overlay'),  // Theme-aware overlay surface
                  borderRadius: '4px',        // Rounded corners
                  boxShadow: token('elevation.shadow.overlay'),  // Theme-aware shadow
                  minWidth: '150px',          // Minimum width for readability
                  padding: '4px 0',           // Vertical padding
                }}
              >
              {/*
                DROPDOWN ITEMS

                Map through OTHER statuses (excluding Pass/Fail since they have quick action buttons)
                Each item shows:
                - Status icon (colored based on status type)
                - Status name
                - Hover effect (light gray background)
              */}
              {otherStatuses.map(status => {
                // Get the icon component and color for this status
                const DropdownStatusIcon = getStatusIcon(status.name)
                const dropdownIconColor = getStatusIconColor(status.name, status.color)

                return (
                  <div
                    key={status.id}
                    onClick={(e) => {
                      e.stopPropagation()           // Prevent row click
                      handleStatusClick(status)     // Set the selected status
                      setIsDropdownOpen(false)      // Close the dropdown
                    }}
                    onMouseDown={(e) => e.stopPropagation()}  // Prevent mousedown propagation
                    className="dropdown-status-item"
                    style={{
                      display: 'flex',              // Flexbox for horizontal layout
                      alignItems: 'center',         // Vertically center items
                      gap: '8px',                   // Space between icon and text
                      padding: '8px 12px',          // Comfortable padding
                      cursor: 'pointer',            // Show it's clickable
                      transition: 'background-color 0.1s'  // Smooth hover transition
                    }}
                    // Inline event handlers for hover effects (lightweight approach)
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = token('color.background.neutral.hovered')  // Theme-aware hover
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'  // Clear on mouse leave
                    }}
                  >
                    {/* STATUS ICON */}
                    <DropdownStatusIcon
                      label={status.name}
                      size="small"
                      primaryColor={dropdownIconColor}
                    />

                    {/* STATUS NAME */}
                    <span style={{ fontSize: '14px' }}>{status.name}</span>
                  </div>
                )
              })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
